import { getSanitizeStyleBreadcrumb, sanitizeLogger } from './sanitizeLogger';
import { parse } from 'postcss';
import * as valueParserModule from 'postcss-value-parser';
import { CONFIG_SANITIZE_STYLE } from './config';
const valueParser = valueParserModule.default || valueParserModule;
/** Общая логика очистки: проход по декларациям в правиле */
const sanitizeRuleDecls = (rule, styleOrCss) => {
    rule.walkDecls((declaration) => {
        var _a;
        const property = declaration.prop;
        const value = String((_a = declaration.value) !== null && _a !== void 0 ? _a : '');
        const { FORBIDDEN_PROPERTIES, FORBIDDEN_VALUES, SAFE_DATA_IMAGE, SAFE_URL } = CONFIG_SANITIZE_STYLE;
        /** Удаляет декларацию если есть запрещенные свойства  */
        if (FORBIDDEN_PROPERTIES.some(regexp => regexp.test(property))) {
            sanitizeLogger(getSanitizeStyleBreadcrumb(property, 'property', styleOrCss));
            declaration.remove();
            return;
        }
        /** Удаляет декларацию если есть запрещенные значения  */
        if (FORBIDDEN_VALUES.some(regexp => regexp.test(value))) {
            sanitizeLogger(getSanitizeStyleBreadcrumb(value, 'value', styleOrCss));
            declaration.remove();
            return;
        }
        /** Проверка url(...) на запрещенные значения */
        let unsafe = false;
        /** Парсинг значение декларации на ноды */
        const parsed = valueParser(value);
        parsed.walk((node) => {
            if (node.type === 'function' && node.value.toLowerCase() === 'url') {
                /** Собирает ноды в чистое содержимое из url(...) */
                const inner = valueParser
                    .stringify(node.nodes)
                    .trim()
                    .replace(/^['"]|['"]$/g, '');
                /** Ищет опасные типы data */
                if (/^data:/i.test(inner)) {
                    if (!SAFE_DATA_IMAGE.test(inner)) {
                        unsafe = true;
                        return false;
                    }
                    /** Ищет опасные url */
                }
                else if (!SAFE_URL.test(inner)) {
                    unsafe = true;
                    return false;
                }
            }
            /** Продолжить обход */
            return undefined;
        });
        /** Удаляет декларацию, если есть небезопасные значения */
        if (unsafe) {
            sanitizeLogger(getSanitizeStyleBreadcrumb(value, 'url', styleOrCss));
            declaration.remove();
            return;
        }
    });
    /** Если после очистки, селектор оказался пустой, он полностью удаляется */
    if (!rule.nodes || rule.nodes.length === 0) {
        rule.remove();
    }
};
/** Сериализация inline-стилей из правила: "prop: value; prop2: value2" */
const serializeInlineFromRule = (rule) => {
    var _a;
    const declarations = [];
    (_a = rule.nodes) === null || _a === void 0 ? void 0 : _a.forEach(node => {
        if (node.type === 'decl') {
            declarations.push(`${node.prop}: ${node.value}`);
        }
    });
    return declarations.length ? declarations.join('; ') : null;
};
/**
 * Чистит inline style, оставляя только безопасные.
 *
 * - Если содержащие опасные или невалидные конструкции (expression, `@import`, javascript: и т.п.), возвращает `null`.
 * - Для свойств с url(...) проверяет каждую ссылку по SAFE_URL, небезопасные — вырезает.
 * - Если после фильтрации не остаётся ни одного объявления, возвращает `null`.
 *
 * @param style Строка со значением атрибута style (например, "color:red;background:url(...)").
 * @returns Строка с безопасными style или `null`, если ничего не осталось.
 */
export const sanitizeInlineStyle = (style) => {
    if (!(style === null || style === void 0 ? void 0 : style.trim())) {
        return null;
    }
    let root;
    /**
     * Ошибка при парсинге может быть, если внутри inline style,
     * есть опасные или невалидные декларации, значения, которые parse не может распарсить.
     * В таком случае отбрасываем весь атрибут style.
     */
    try {
        root = parse(`#x{${style}}`);
    }
    catch (e) {
        sanitizeLogger(getSanitizeStyleBreadcrumb(`${e}`, 'parse', style));
        return null;
    }
    const rule = root.first;
    if ((rule === null || rule === void 0 ? void 0 : rule.type) !== 'rule') {
        return null;
    }
    sanitizeRuleDecls(rule, style);
    return serializeInlineFromRule(rule);
};
/**
 * Чистит полноценный CSS (содержимое <style>).
 * - удаляет комментарии
 * - оставляет только allowlisted @-правила (по умолчанию: @media, @keyframes)
 * - вырезает декларации с опасными значениями и небезопасными url(...)
 * - удаляет пустые правила
 *
 * @param css Строка со значением содердимого тега `<style>`
 * @returns Строка с безопасными css или `null`, если ничего не осталось.
 */
export const sanitizeStyleBlock = (css) => {
    if (!(css === null || css === void 0 ? void 0 : css.trim())) {
        return '';
    }
    const { ALLOW_AT_RULES, FORBIDDEN_AT_RULES } = CONFIG_SANITIZE_STYLE;
    let root;
    /**
     * Ошибка при парсинге может быть, если внутри тега <style>,
     * есть опасные или невалидные декларации, значения, которые parse не может распарсить.
     * В таком случае отбрасываем весь тег <style>.
     */
    try {
        root = parse(css);
    }
    catch (e) {
        sanitizeLogger(getSanitizeStyleBreadcrumb(`${e}`, 'parse', css));
        return null;
    }
    /** Удаляет все комментарии */
    root.walkComments(comment => comment.remove());
    /** Проходится по всем @-правилам */
    root.walkAtRules((at) => {
        const name = at.name.toLowerCase();
        /** Удаляет явно опасные */
        if (FORBIDDEN_AT_RULES.includes(name)) {
            sanitizeLogger(getSanitizeStyleBreadcrumb(name, 'rule', css));
            at.remove();
        }
        /** Пропускает только разрешенные */
        if (ALLOW_AT_RULES.includes(name)) {
            return;
        }
        /** Удаляет все остальные неизвестные */
        sanitizeLogger(getSanitizeStyleBreadcrumb(name, 'rule', css));
        at.remove();
    });
    /** Проходится по всем селекторам */
    root.walkRules((rule) => {
        sanitizeRuleDecls(rule, css);
    });
    return root.toString().trim();
};
